"use client";

import { usePath, useSearch } from "@packages/framework";
import type { SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";

export type UseSearchParamStateProps<
  OptionsMultipleType extends boolean = false,
  ValueType extends string = string,
> = {
  defaultValue?: OptionsMultipleType extends true ? ValueType[] : ValueType;
  key: string;
  multiple?: OptionsMultipleType;
  removeFromUrlIfDefaultValue?: boolean;
};

export const useSearchParamState = <
  OptionsMultipleType extends boolean = false,
  ValueType extends string = string,
>({
  defaultValue,
  key,
  multiple,
  removeFromUrlIfDefaultValue,
}: UseSearchParamStateProps<OptionsMultipleType>) => {
  const path = usePath();
  const search = useSearch();
  const forceReplace = useRef<boolean>(false);

  const [state, _setState] = useState<
    UseSearchParamStateProps<OptionsMultipleType>["defaultValue"]
  >(
    (multiple
      ? search.getAll(key)?.length > 0
        ? search.getAll(key)
        : (defaultValue ?? [])
      : search.get(key)?.length > 0
        ? search.get(key)
        : (defaultValue ??
          "")) as UseSearchParamStateProps<OptionsMultipleType>["defaultValue"],
  );

  const setState = (
    stateSetter: SetStateAction<
      OptionsMultipleType extends true ? string[] : string
    >,
    replace = false,
  ) => {
    forceReplace.current = replace;
    _setState(stateSetter);
  };

  useEffect(() => {
    // Read the live URL rather than the `search` closure: another
    // useSearchParamState instance (or any other raw history writer) may have
    // written to the URL after this render captured `search`. Building from a
    // stale snapshot here would silently drop that concurrent write when we
    // rewrite the full query string below.
    const currentQueryString =
      typeof window !== "undefined"
        ? window.location.search.replace(/^\?/, "")
        : search.toString();
    const newSearchParams = new URLSearchParams(currentQueryString);

    const old = multiple
      ? newSearchParams.getAll(key)
      : newSearchParams.get(key);
    const replace =
      (!old && !removeFromUrlIfDefaultValue) || forceReplace.current;

    // update search params IF either there was none and we want to show it,
    // OR there was none and state is no longer the default value,
    // OR there was one but it is different from what state is now
    const update =
      (!old && (!removeFromUrlIfDefaultValue || state !== defaultValue)) ||
      JSON.stringify(old) !== JSON.stringify(state);

    if (update) {
      newSearchParams.delete(key);

      if (
        !(
          removeFromUrlIfDefaultValue &&
          JSON.stringify(defaultValue) === JSON.stringify(state)
        )
      ) {
        if (multiple && state.length !== 0)
          (state as string[]).forEach((stateValue) => {
            if (stateValue.length > 0) newSearchParams.append(key, stateValue);
          });
        else if (state.length > 0) newSearchParams.set(key, state as string);
      }

      newSearchParams.sort();

      const newQueryString = newSearchParams.toString();
      if (
        typeof history !== "undefined" &&
        typeof window !== "undefined" &&
        newQueryString !== currentQueryString
      ) {
        const newPath = `${path}${newQueryString ? "?" + newQueryString : ""}`;
        const url = new URL(newPath, window.location.href);
        if (replace) {
          history.replaceState(null, "", url);
        } else {
          history.pushState(null, "", url);
        }
      }
    }
  }, [state]);

  useEffect(() => {
    const paramValue = multiple
      ? search.getAll(key)?.length > 0
        ? search.getAll(key)
        : (defaultValue ?? [])
      : search.get(key)?.length > 0
        ? search.get(key)
        : (defaultValue ?? "");

    if (JSON.stringify(paramValue) !== JSON.stringify(state)) {
      setState(
        paramValue as UseSearchParamStateProps<OptionsMultipleType>["defaultValue"],
      );
    }
  }, [search]);

  return [state, setState] as [
    UseSearchParamStateProps<OptionsMultipleType, ValueType>["defaultValue"],
    (
      setter: SetStateAction<
        UseSearchParamStateProps<OptionsMultipleType, ValueType>["defaultValue"]
      >,
      replaceHistory?: boolean,
    ) => void,
  ];
};
